import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import BookingDetailsModal from './BookingDetailsModal';
import './ArtisanSchedule.css';

export default function ArtisanSchedule() {
    const [activeTab, setActiveTab] = useState('upcoming');
    const [upcomingBookings, setUpcomingBookings] = useState([]);
    const [pendingBookings, setPendingBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [completionModal, setCompletionModal] = useState(null);
    const [paymentCollected, setPaymentCollected] = useState(false);

    useEffect(() => {
        fetchSchedule();
    }, []);

    const fetchSchedule = async () => {
        setLoading(true);
        try {
            // Fetch Upcoming (Confirmed)
            const resUpcoming = await api.get('/api/artisan/bookings/upcoming');
            if (resUpcoming.ok) {
                const data = await resUpcoming.json();
                setUpcomingBookings(data);
            }

            // Fetch Pending (Reuse existing endpoint or filter client side if needed)
            // ideally we should have a dedicated endpoint but for MVP using generic GET
            const resAll = await api.get('/api/bookings?role=artisan');
            if (resAll.ok) {
                const all = await resAll.json();
                const pending = all.filter(b => b.status === 'PENDING_APPROVAL');
                setPendingBookings(pending);
            }
        } catch (err) {
            console.error("Failed to fetch schedule", err);
            setError("Failed to load schedule.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenMap = (address) => {
        const encoded = encodeURIComponent(address);
        // Opens native map on mobile or Google Maps on web
        window.open(`https://maps.google.com/?q=${encoded}`, '_blank');
    };

    const handleOpenCompletionModal = (booking) => {
        setCompletionModal(booking);
        setPaymentCollected(false);
    };

    const confirmCompletion = async () => {
        if (!completionModal) return;

        try {
            // Helper to encode ID if not already (assuming booking_id is present or constructed)
            const id = completionModal.booking_id || completionModal.bookingId;

            const res = await api.post(`/api/artisan/bookings/${id}/complete`, {});
            if (res.ok) {
                // Remove from upcoming list (or move to past if we had that tab)
                setUpcomingBookings(prev => prev.filter(b =>
                    (b.booking_id && b.booking_id !== id) || (b.bookingId && b.bookingId !== id)
                ));
                setCompletionModal(null);
                //alert("Job marked as completed! 🎉");
            } else {
                const err = await res.json();
                alert(`Failed to complete job: ${err.message || 'Unknown error'}`);
            }
        } catch (e) {
            console.error(e);
            alert("Network error completing job.");
        }
    };

    const renderBookingCard = (booking, isPending = false) => {
        return (
            <div key={booking.booking_id || booking.bookingId} className={`booking-card ${isPending ? 'pending-card' : ''}`}>
                <div className="card-header">
                    <span className="booking-time">
                        {booking.date} • {booking.start_time || booking.startTime?.substring(11, 16)}
                    </span>
                    {isPending && <span className="badge-pending">Action Required</span>}
                </div>

                <div className="card-body">
                    <h3 className="customer-name">{booking.customer_name || booking.customerName || 'Customer'}</h3>
                    <p className="service-name">{booking.package_name || booking.serviceName}</p>

                    <div className="location-row" onClick={() => handleOpenMap(booking.address || booking.location?.address)}>
                        <span className="icon">📍</span>
                        <span className="address-text">{booking.address || booking.location?.address || 'No Location'}</span>
                    </div>
                </div>

                <div className="card-footer">
                    <span className="payout">💰 ${(booking.payout_cents / 100 || 104).toFixed(2)}</span>
                    {isPending ? (
                        <div className="action-buttons">
                            <button className="btn btn-sm btn-decline">Decline</button>
                            <button className="btn btn-sm btn-accept">Accept</button>
                        </div>
                    ) : (
                        <div className="action-buttons">
                            <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleOpenMap(booking.address || booking.location?.address)}
                            >
                                📍 Navigate
                            </button>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => setCompletionModal(booking)}
                            >
                                View Details
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    if (loading) return <div className="schedule-loading">Loading schedule...</div>;
    if (error) return <div className="schedule-error">{error}</div>;

    return (
        <div className="artisan-schedule-container">
            <div className="schedule-tabs">
                <button
                    className={`tab-btn ${activeTab === 'upcoming' ? 'active' : ''}`}
                    onClick={() => setActiveTab('upcoming')}
                >
                    Upcoming ({upcomingBookings.length})
                </button>
                <button
                    className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending ({pendingBookings.length})
                </button>
            </div>

            <div className="schedule-content">
                {activeTab === 'upcoming' ? (
                    upcomingBookings.length > 0 ? (
                        <div className="bookings-list">
                            {upcomingBookings.map(b => renderBookingCard(b, false))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <div className="empty-illustration">📅</div>
                            <h3>Your schedule is clear</h3>
                            <p>Check back later for new requests!</p>
                        </div>
                    )
                ) : (
                    pendingBookings.length > 0 ? (
                        <div className="bookings-list">
                            {pendingBookings.map(b => renderBookingCard(b, true))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <h3>No pending requests</h3>
                            <p>You're all caught up!</p>
                        </div>
                    )
                )}
            </div>

            {/* Details Modal */}
            {completionModal && (
                <BookingDetailsModal
                    booking={completionModal}
                    onClose={() => setCompletionModal(null)}
                    onComplete={confirmCompletion}
                />
            )}
        </div>
    );
}
